import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Plus, X, ChevronLeft, ChevronRight, AlertTriangle } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { hasRole, schoolIdFromUser } from '../lib/rbac';

const C = {
  yellow: '#F7D000', black: '#1A1A1A', white: '#FFFFFF',
  border: '#E5E7EB', gray100: '#F3F4F6', gray500: '#6B7280',
  gray700: '#374151', gray900: '#111827',
  success: '#166534', successBg: '#DCFCE7',
  error: '#991B1B', errorBg: '#FEE2E2',
  warning: '#92400E', warningBg: '#FEF3C7',
  info: '#1E40AF', infoBg: '#DBEAFE',
  slate: '#334155', slateBg: '#F1F5F9',
};

const BN = {
  title: 'Perancangan Persaraan & Penamatan', subtitle: 'rekod', back: '← Laman Utama',
  addRecord: '+ Tambah Rekod', tabs: ['Rekod', 'Dalam 12 Bulan', 'Statistik'] as const,
  allType: 'Semua Jenis', allStatus: 'Semua Status',
  prev: 'Sebelum', next: 'Seterusnya', showing: 'Menunjukkan', to: 'hingga', of: 'daripada',
  emptyTitle: 'Tiada Rekod', emptyBody: 'Tiada rekod persaraan direkodkan.',
  addTitle: 'Rekod Persaraan/Penamatan',
  educatorId: 'ID Pendidik *', exitType: 'Jenis Penamatan *',
  expectedDate: 'Tarikh Jangkaan *', actualDate: 'Tarikh Sebenar',
  retirementGrade: 'Gred Persaraan', gratuity: 'Ganjaran (BND)', pension: 'Pencen (BND/bulan)',
  handoverNotes: 'Nota Serah Tugas', replacementPlan: 'Rancangan Penggantian',
  save: 'Simpan', saving: 'Menyimpan…', cancel: 'Batal',
  confirm: 'Sahkan', process: 'Proses',
  total: 'Jumlah', upcoming12m: 'Dalam 12 Bulan', processed: 'Selesai Diproses',
  retry: 'Cuba Lagi', errLoad: 'Gagal memuatkan.',
  daysLeft: 'hari lagi',
};
const EN = {
  title: 'Retirement & Exit Planning', subtitle: 'records', back: '← Home',
  addRecord: '+ Add Record', tabs: ['Records', 'Upcoming 12M', 'Statistics'] as const,
  allType: 'All Types', allStatus: 'All Status',
  prev: 'Previous', next: 'Next', showing: 'Showing', to: 'to', of: 'of',
  emptyTitle: 'No Records', emptyBody: 'No retirement records recorded.',
  addTitle: 'Record Retirement/Exit',
  educatorId: 'Educator ID *', exitType: 'Exit Type *',
  expectedDate: 'Expected Date *', actualDate: 'Actual Date',
  retirementGrade: 'Retirement Grade', gratuity: 'Gratuity (BND)', pension: 'Pension (BND/month)',
  handoverNotes: 'Handover Notes', replacementPlan: 'Replacement Plan',
  save: 'Save', saving: 'Saving…', cancel: 'Cancel',
  confirm: 'Confirm', process: 'Process',
  total: 'Total', upcoming12m: 'Next 12 Months', processed: 'Processed',
  retry: 'Retry', errLoad: 'Failed to load.',
  daysLeft: 'days remaining',
};

const EXIT_TYPES = ['MANDATORY_RETIREMENT','VOLUNTARY_RETIREMENT','EARLY_RETIREMENT','RESIGNATION','CONTRACT_END','MEDICAL_TERMINATION','DECEASED'];
const EXIT_CFG: Record<string, { ms: string; en: string; color: string; bg: string }> = {
  MANDATORY_RETIREMENT: { ms: 'Persaraan Wajib',     en: 'Mandatory Retirement',  color: '#1E40AF', bg: '#DBEAFE' },
  VOLUNTARY_RETIREMENT: { ms: 'Persaraan Sukarela',  en: 'Voluntary Retirement',  color: '#166534', bg: '#DCFCE7' },
  EARLY_RETIREMENT:     { ms: 'Persaraan Awal',      en: 'Early Retirement',      color: '#0F766E', bg: '#CCFBF1' },
  RESIGNATION:          { ms: 'Letak Jawatan',       en: 'Resignation',           color: '#92400E', bg: '#FEF3C7' },
  CONTRACT_END:         { ms: 'Tamat Kontrak',       en: 'Contract End',          color: '#B45309', bg: '#FEF9C3' },
  MEDICAL_TERMINATION:  { ms: 'Penamatan Perubatan', en: 'Medical Termination',   color: '#991B1B', bg: '#FEE2E2' },
  DECEASED:             { ms: 'Kematian',            en: 'Deceased',              color: '#4B5563', bg: '#F3F4F6' },
};
const STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  PLANNED:   { label: 'Dirancang',   color: '#92400E', bg: '#FEF3C7' },
  CONFIRMED: { label: 'Disahkan',   color: '#1E40AF', bg: '#DBEAFE' },
  PROCESSED: { label: 'Selesai',    color: '#166534', bg: '#DCFCE7' },
  CANCELLED: { label: 'Dibatalkan', color: '#4B5563', bg: '#F3F4F6' },
};

interface Props { user: AuthUser }
interface RetirementRecord {
  id: string; educator_id: string; exit_type: string; expected_date: string;
  actual_date: string | null; retirement_grade: string | null;
  gratuity: number | null; pension: number | null;
  handover_notes: string | null; replacement_plan: string | null; status: string;
  full_name_en: string | null; full_name_ms: string | null;
  service_number: string; designation: string | null; school_name: string | null;
}
interface PageResult { data: RetirementRecord[]; total: number; page: number }
const LIMIT = 20;
const cardShadow = '0 1px 3px rgba(0,0,0,0.08)';
const today = new Date();

function daysUntil(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function RetirementPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;
  usePageTitle(UI.title);

  const canEdit  = hasRole(user, 'super_admin', 'admin', 'principal', 'clerk');
  const schoolId = schoolIdFromUser(user) ?? '';

  const [tab,     setTab]     = useState<'records' | 'upcoming' | 'stats'>('records');
  const [records, setRecords] = useState<RetirementRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [page,    setPage]    = useState(1);
  const [total,   setTotal]   = useState(0);
  const [typeFilter,   setTypeFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [form, setForm] = useState({
    educatorId: '', exitType: 'MANDATORY_RETIREMENT', expectedDate: '', actualDate: '',
    retirementGrade: '', gratuity: '', pension: '',
    handoverNotes: '', replacementPlan: '',
  });

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ page: String(page) });
      if (schoolId)    p.set('schoolId', schoolId);
      if (typeFilter)   p.set('exitType', typeFilter);
      if (statusFilter) p.set('status', statusFilter);
      if (tab === 'upcoming') p.set('upcoming12m', 'true');
      const res = await apiGet<PageResult>(`/api/educators/retirement?${p}`);
      setRecords(res.data); setTotal(res.total);
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, page, typeFilter, statusFilter, tab, UI.errLoad]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.educatorId.trim() || !form.expectedDate) return;
    setSaving(true);
    try {
      await apiPost('/api/educators/retirement', {
        educatorId: form.educatorId, exitType: form.exitType, expectedDate: form.expectedDate,
        actualDate: form.actualDate || undefined,
        retirementGrade: form.retirementGrade || undefined,
        gratuity: form.gratuity ? parseFloat(form.gratuity) : undefined,
        pension: form.pension ? parseFloat(form.pension) : undefined,
        handoverNotes: form.handoverNotes || undefined,
        replacementPlan: form.replacementPlan || undefined,
      });
      setShowAdd(false);
      setForm({ educatorId: '', exitType: 'MANDATORY_RETIREMENT', expectedDate: '', actualDate: '', retirementGrade: '', gratuity: '', pension: '', handoverNotes: '', replacementPlan: '' });
      setToast({ message: lang === 'en' ? 'Record created.' : 'Rekod dicipta.', variant: 'success' });
      load();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await apiPatch(`/api/educators/retirement/${id}/status`, { status });
      setToast({ message: lang === 'en' ? 'Status updated.' : 'Status dikemas kini.', variant: 'success' });
      load();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '14px', color: C.gray900, backgroundColor: C.white, boxSizing: 'border-box' };
  const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '4px', display: 'block' };

  const upcoming = records.filter(r => { const d = daysUntil(r.expected_date); return d >= 0 && d <= 365; });

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
      <div style={{ marginBottom: '24px' }}>
        <Link to="/" style={{ fontSize: '13px', color: C.gray500, textDecoration: 'none' }}>{UI.back}</Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: C.slateBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Briefcase size={20} color={C.slate} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: C.gray900 }}>{UI.title}</h1>
              <p style={{ margin: 0, fontSize: '13px', color: C.gray500 }}>{total} {UI.subtitle}</p>
            </div>
          </div>
          {canEdit && <button onClick={() => setShowAdd(true)} style={{ padding: '9px 16px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: C.black }}>{UI.addRecord}</button>}
        </div>
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: UI.total, value: total, color: C.slate, bg: C.slateBg },
          { label: UI.upcoming12m, value: upcoming.length, color: C.warning, bg: C.warningBg },
          { label: UI.processed, value: records.filter(r => r.status === 'PROCESSED').length, color: C.success, bg: C.successBg },
        ].map(card => (
          <div key={card.label} style={{ background: card.bg, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '28px', fontWeight: 800, color: card.color }}>{card.value}</div>
            <div style={{ fontSize: '12px', color: card.color }}>{card.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: `1px solid ${C.border}` }}>
        {(['records', 'upcoming', 'stats'] as const).map((t, i) => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '10px 20px', border: 'none', background: 'transparent', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: tab === t ? C.black : C.gray500, borderBottom: tab === t ? `2px solid ${C.yellow}` : '2px solid transparent', marginBottom: '-1px' }}>
            {lang === 'en' ? EN.tabs[i] : BN.tabs[i]}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[
          { val: typeFilter, set: setTypeFilter, opts: EXIT_TYPES.map(t => ({ v: t, l: lang === 'en' ? EXIT_CFG[t]?.en ?? t : EXIT_CFG[t]?.ms ?? t })), placeholder: UI.allType },
          { val: statusFilter, set: setStatusFilter, opts: Object.keys(STATUS_CFG).map(s => ({ v: s, l: STATUS_CFG[s]!.label })), placeholder: UI.allStatus },
        ].map(({ val, set, opts, placeholder }) => (
          <select key={placeholder} value={val} onChange={e => { set(e.target.value); setPage(1); }} style={{ ...inputStyle, width: '200px' }}>
            <option value="">{placeholder}</option>
            {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '40px', color: C.gray500 }}>…</div>}
      {error && <div style={{ padding: '16px', background: C.errorBg, color: C.error, borderRadius: '8px', marginBottom: '16px' }}>{error}</div>}

      {!loading && records.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
          <Briefcase size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
          <p style={{ fontWeight: 600, margin: 0 }}>{UI.emptyTitle}</p>
        </div>
      )}

      <div style={{ display: 'grid', gap: '12px' }}>
        {records.map(r => {
          const ec = EXIT_CFG[r.exit_type];
          const sc = STATUS_CFG[r.status] ?? STATUS_CFG['PLANNED']!;
          const days = daysUntil(r.expected_date);
          const isUrgent = days >= 0 && days <= 90;
          return (
            <div key={r.id} style={{ background: C.white, border: `1px solid ${isUrgent ? '#FCD34D' : C.border}`, borderRadius: '10px', padding: '16px', boxShadow: cardShadow }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    {isUrgent && <AlertTriangle size={14} color={C.warning} />}
                    <span style={{ fontWeight: 700, fontSize: '15px', color: C.gray900 }}>
                      {lang === 'en' ? (r.full_name_en ?? r.service_number) : (r.full_name_ms ?? r.full_name_en ?? r.service_number)}
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: C.gray700 }}>
                    {r.designation ?? r.service_number}{r.school_name ? ` · ${r.school_name}` : ''}
                  </div>
                  <div style={{ fontSize: '12px', color: C.gray500, marginTop: '2px' }}>
                    {lang === 'en' ? 'Expected:' : 'Dijangka:'} {new Date(r.expected_date).toLocaleDateString('ms-BN')}
                    {days >= 0 ? ` (${days} ${UI.daysLeft})` : ''}
                    {r.actual_date ? ` · ${lang === 'en' ? 'Actual:' : 'Sebenar:'} ${new Date(r.actual_date).toLocaleDateString('ms-BN')}` : ''}
                  </div>
                  {(r.gratuity || r.pension) && (
                    <div style={{ fontSize: '12px', color: C.gray500, marginTop: '2px' }}>
                      {r.gratuity ? `${lang === 'en' ? 'Gratuity:' : 'Ganjaran:'} BND ${r.gratuity.toFixed(2)} ` : ''}
                      {r.pension ? `· ${lang === 'en' ? 'Pension:' : 'Pencen:'} BND ${r.pension.toFixed(2)}/mo` : ''}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0, marginLeft: '12px' }}>
                  <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: ec?.bg, color: ec?.color }}>{lang === 'en' ? ec?.en : ec?.ms}</span>
                  <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: sc.bg, color: sc.color }}>{sc.label}</span>
                </div>
              </div>
              {r.replacement_plan && <div style={{ fontSize: '12px', color: C.gray700, marginTop: '6px', padding: '8px', background: C.slateBg, borderRadius: '6px' }}>🔄 {r.replacement_plan}</div>}
              {canEdit && r.status === 'PLANNED' && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button onClick={() => updateStatus(r.id, 'CONFIRMED')} style={{ padding: '5px 14px', borderRadius: '6px', background: C.infoBg, border: 'none', color: C.info, fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>{UI.confirm}</button>
                </div>
              )}
              {canEdit && r.status === 'CONFIRMED' && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                  <button onClick={() => updateStatus(r.id, 'PROCESSED')} style={{ padding: '5px 14px', borderRadius: '6px', background: C.successBg, border: 'none', color: C.success, fontWeight: 600, fontSize: '12px', cursor: 'pointer' }}>{UI.process}</button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {total > LIMIT && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', fontSize: '13px', color: C.gray500 }}>
          <span>{UI.showing} {(page - 1) * LIMIT + 1} {UI.to} {Math.min(page * LIMIT, total)} {UI.of} {total}</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}><ChevronLeft size={14} /></button>
            <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page * LIMIT >= total ? 'default' : 'pointer', opacity: page * LIMIT >= total ? 0.5 : 1 }}><ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {showAdd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '540px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{UI.addTitle}</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>{UI.educatorId}</label>
                <input type="text" value={form.educatorId} onChange={e => setForm(p => ({ ...p, educatorId: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>{UI.exitType}</label>
                <select value={form.exitType} onChange={e => setForm(p => ({ ...p, exitType: e.target.value }))} style={inputStyle}>
                  {EXIT_TYPES.map(t => <option key={t} value={t}>{lang === 'en' ? EXIT_CFG[t]?.en : EXIT_CFG[t]?.ms}</option>)}
                </select>
              </div>
              {[
                { key: 'expectedDate', label: UI.expectedDate, type: 'date' },
                { key: 'actualDate', label: UI.actualDate, type: 'date' },
                { key: 'retirementGrade', label: UI.retirementGrade, type: 'text' },
                { key: 'gratuity', label: UI.gratuity, type: 'number' },
                { key: 'pension', label: UI.pension, type: 'number' },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={form[f.key as keyof typeof form]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))} style={inputStyle} />
                </div>
              ))}
              {[
                { key: 'handoverNotes', label: UI.handoverNotes },
                { key: 'replacementPlan', label: UI.replacementPlan },
              ].map(f => (
                <div key={f.key} style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>{f.label}</label>
                  <textarea value={form[f.key as keyof typeof form]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ ...inputStyle, height: '70px', resize: 'vertical' }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={submit} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? UI.saving : UI.save}
              </button>
              <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.gray100, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer' }}>{UI.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
